/** True when the storey has no mapped triangles / is marked empty. */
export function isEmptyStorey(storey) {
  if (!storey) return true;
  if (storey.empty === true) return true;
  if (storey.hasGeometry === false) return true;
  if (storey.no_geometry === true || storey.noGeometry === true) return true;
  return false;
}

/** Datum storeys that actually have geometry — used by «Koble tegning». */
export function alignableStoreys(storeys) {
  return (Array.isArray(storeys) ? storeys : []).filter((row) => !isEmptyStorey(row));
}

/**
 * Map a PDF point (top-left origin, Y down — same as the click dialog)
 * through the overlay similarity into visual IFC XY.
 */
export function pdfToIfcOverlay(px, py, transform, pageWidth, pageHeight) {
  const scale = transform.scale;
  const theta = ((transform.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const localX = (px - pageWidth / 2) * scale;
  const localY = (pageHeight / 2 - py) * scale;
  return {
    x: transform.x + cos * localX - sin * localY,
    y: transform.y + sin * localX + cos * localY,
  };
}

/**
 * Two control points that encode the same similarity the API already stores
 * (`drawing_ifc_alignments.control_points` → scale + rotation + translation).
 */
export function overlayToControlPoints(transform, pageWidth, pageHeight) {
  const width = Number(pageWidth) || 1;
  const height = Number(pageHeight) || 1;
  return [
    { pdf: { x: 0, y: 0 }, ifc: pdfToIfcOverlay(0, 0, transform, width, height) },
    { pdf: { x: width, y: 0 }, ifc: pdfToIfcOverlay(width, 0, transform, width, height) },
  ];
}

export const LINK_DRAWING_PDF_KIND = {
  missing: "missing",
  fetch: "fetch",
  raster: "raster",
};

export class LinkDrawingPdfError extends Error {
  constructor(kind, cause) {
    super(kind);
    this.name = "LinkDrawingPdfError";
    this.kind = kind;
    this.cause = cause;
  }
}

export function linkDrawingPdfErrorKey(kind) {
  if (kind === LINK_DRAWING_PDF_KIND.missing) {
    return "ifc_viewer.link_drawing_pdf_missing";
  }
  if (kind === LINK_DRAWING_PDF_KIND.fetch) {
    return "ifc_viewer.link_drawing_pdf_fetch_error";
  }
  if (kind === LINK_DRAWING_PDF_KIND.raster) {
    return "ifc_viewer.link_drawing_pdf_raster_error";
  }
  return "ifc_viewer.link_drawing_pdf_error";
}

function ensurePdfWorker(pdfjs) {
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }
}

function asPdfBytes(source) {
  if (source instanceof Uint8Array) return source;
  if (source instanceof ArrayBuffer) return new Uint8Array(source);
  return null;
}

/**
 * Load PDF bytes for rasterize. Absolute S3/signed URLs use fetch (no extra
 * headers). Relative / API ActiveStorage URLs use axios so the bearer token
 * is sent. Never pass a blob: URL into the pdf.js worker — unpkg workers
 * cannot read those and rasterize throws.
 */
export async function fetchPdfBytes(pdfFileUrl) {
  if (!pdfFileUrl) throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.missing);
  const isAbsolute =
    typeof pdfFileUrl === "string" &&
    (pdfFileUrl.startsWith("http://") || pdfFileUrl.startsWith("https://"));
  const axios = (await import("../../../api/axios")).default;
  if (!isAbsolute) {
    try {
      const res = await axios({
        method: "get",
        url: pdfFileUrl,
        responseType: "arraybuffer",
      });
      return new Uint8Array(res.data);
    } catch (err) {
      throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.fetch, err);
    }
  }
  try {
    const res = await fetch(pdfFileUrl);
    if (!res.ok) throw new Error(`http ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  } catch (err) {
    try {
      const res = await axios({
        method: "get",
        url: pdfFileUrl,
        responseType: "arraybuffer",
      });
      return new Uint8Array(res.data);
    } catch (retry) {
      throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.fetch, retry || err);
    }
  }
}

/** Rasterize one PDF page to a JPEG data URL for the Three.js overlay. */
export async function rasterizePdfPage(source, { maxPx = 2048, pageNumber = 1 } = {}) {
  const { pdfjs } = await import("react-pdf");
  ensurePdfWorker(pdfjs);
  let data = asPdfBytes(source);
  if (!data && typeof source === "string") {
    if (source.startsWith("blob:")) {
      const res = await fetch(source);
      if (!res.ok) throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.fetch);
      data = new Uint8Array(await res.arrayBuffer());
    } else {
      data = await fetchPdfBytes(source);
    }
  }
  if (!data?.byteLength) {
    throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.missing);
  }
  const openDoc = (opts) => pdfjs.getDocument({ data: data.slice(), ...opts }).promise;
  let pdf;
  try {
    pdf = await openDoc({});
  } catch (first) {
    try {
      pdf = await openDoc({ useWorkerFetch: false });
    } catch (second) {
      throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.raster, second || first);
    }
  }
  try {
    const page = await pdf.getPage(pageNumber);
    const unscaled = page.getViewport({ scale: 1 });
    const scale = Math.min(maxPx / unscaled.width, maxPx / unscaled.height, 2);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const context = canvas.getContext("2d");
    if (!context) throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.raster);
    await page.render({ canvasContext: context, viewport }).promise;
    return {
      imageDataUrl: canvas.toDataURL("image/jpeg", 0.85),
      pageWidth: unscaled.width,
      pageHeight: unscaled.height,
    };
  } catch (err) {
    if (err instanceof LinkDrawingPdfError) throw err;
    throw new LinkDrawingPdfError(LINK_DRAWING_PDF_KIND.raster, err);
  } finally {
    await pdf.destroy();
  }
}
